<template>
  <Loader v-if="!isDataLoaded" />
  <div v-else>
    <Table
      :title="$t('views.agilityVms.title')"
      :addLabel="$t('views.agilityVms.add')"
      :values="vms"
      :headers="agilityHeaders"
      :rows="agilityRows"
      currentSortKey="name"
      :addPermission="vmCreatePermission"
      :updatePermission="vmUpdatePermission"
      :deletePermission="vmDeletePermission"
      @more="editVm"
      @add="addVm"
      @action="actionVm"
      @export="exportVms"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

import VSPHERE from '@/api/vsphere';

import Table from '@/components/Table.vue';

import { eventHubMixin } from '@/mixins/event';

import Loader from '@/components/Loader.vue';

export default {
  name: 'agilityVms',
  mixins: [eventHubMixin],
  components: {
    Table,
    Loader,
  },
  data() {
    return {
      vms: [],
      isDataLoaded: false,
      agilityHeaders: [
        {
          label: this.$t('views.agilityVms.config.agilityHeaders.name'),
          field: 'name',
          title: this.$t('views.agilityVms.config.agilityHeaders.vm_title'),
        },
        {
          label: this.$t('views.agilityVms.config.agilityHeaders.os'),
          field: 'os',
          title: this.$t('views.agilityVms.config.agilityHeaders.os_title'),
        },

        {
          label: this.$t('views.agilityVms.config.agilityHeaders.vcpu'),
          field: 'vcpu',
          title: this.$t('views.agilityVms.config.agilityHeaders.vcpu_title'),
        },
        {
          label: this.$t('views.agilityVms.config.agilityHeaders.vram'),
          field: 'vram',
          title: this.$t('views.agilityVms.config.agilityHeaders.vram_title'),
        },
        {
          label: this.$t('views.agilityVms.config.agilityHeaders.storage'),
          field: 'storage',
          title: this.$t('views.agilityVms.config.agilityHeaders.storage_title'),
        },
        {
          label: this.$t('views.agilityVms.config.agilityHeaders.classement'),
          field: 'classement',
          title: this.$t('views.agilityVms.config.agilityHeaders.classement_title'),
        },
        {
          label: this.$t('views.agilityVms.config.agilityHeaders.power'),
          field: 'power_state',
          title: this.$t('views.agilityVms.config.agilityHeaders.power_title'),
        },
        {
          label: this.$t('views.agilityVms.config.agilityHeaders.snapshot'),
          field: 'snapshot',
          title: this.$t('views.agilityVms.config.agilityHeaders.snapshot_title'),
        },
        {
          label: this.$t('views.agilityVms.config.agilityHeaders.protected'),
          field: 'protected',
          title: this.$t('views.agilityVms.config.agilityHeaders.protected_title'),
        },
        {
          label: this.$t('views.agilityVms.config.agilityHeaders.boost'),
          field: 'cpu_boost',
          title: this.$t('views.agilityVms.config.agilityHeaders.boost_title'),
        },
        {
          label: this.$t('views.agilityVms.config.agilityHeaders.site'),
          field: 'site',
          title: this.$t('views.agilityVms.config.agilityHeaders.site_title'),
        },
        {
          label: "Power",
        }
      ],
      agilityRows: [
        {
          value: row => row.name,
        },
        {
          value: row => row.os,
        },
        {
          value: row => row.vcpu,
        },
        {
          value: row => row.vram,
        },
        {
          value: row => row.storage,
        },
        {
          icon: row => ((row.vram >= 16) ? 'stars' : 'star'),
          color: (row) => {
            if (row.classement === 'gold') {
              return 'epsilon'
            } if (row.classement === 'silver') {
              return 'beta'
            }
            return 'gamma'
          },
        },
        {
          value: row => ((row.power_state) ? this.$t('views.agilityVms.config.agilityRows.started') : this.$t('views.agilityVms.config.agilityRows.stopped')),
          color: row => ((row.power_state) ? '' : 'gray-700'),
          classList: 'is-xsmall',
        },
        {
          value: row => row.snapshot,
          icon: row => ((row.snapshot) ? '' : 'minus'),
          color: (row) => {
            if (row.snapshot) {
              const match = row.snapshot.match(/.+?(?=d)/)[0];
              if (match > 5) {
                return 'danger';
              } if (match > 2) {
                return 'warning';
              }
              return '';
            }
            return 'danger';
          },
        },
        {
          icon: row => ((row.protected) ? 'valid-round-inverse' : 'minus'),
          color: row => ((row.protected) ? 'success' : 'gray-300'),
        },
        {
          icon: row => ((row.cpu_boost) ? 'valid-round-inverse' : 'minus'),
          color: row => ((row.cpu_boost) ? 'success' : 'gray-300'),
        },
        {
          value: row => row.site,
        }
      ],
    }
  },
  computed: {
    ...mapGetters({
      user: 'auth/user',
      hasSignedAdditionalContract: 'auth/hasSignedAdditionalContract'
    }),

    vmCreatePermission() {
      if ((this.user.is_admin || this.user.roles_obj.some(role => role.opening_vm_creation_ticket)) && this.hasSignedAdditionalContract) {
        return true
      }
      return false
    },
    vmUpdatePermission() {
      if ((this.user.is_admin || this.user.roles_obj.some(role => role.opening_vm_update_ticket)) && this.hasSignedAdditionalContract) {
        return true
      }
      return false
    },
    vmDeletePermission() {
      if ((this.user.is_admin || this.user.roles_obj.some(role => role.opening_vm_delete_ticket)) && this.hasSignedAdditionalContract) {
        return true
      }
      return false
    }
  },
  created() {
    this.listVms();
  },
  methods: {
    listVms() {
      VSPHERE.list().then((res) => {
        this.isDataLoaded = true
        if (res.data && res.data.length) {
          this.vms = res.data
        }
      });
    },

    addVm() {
      this.emitOpenModal('add-edit-vm');
    },

    editVm(data) {
      this.emitOpenModal('add-edit-vm', data);
    },

    deleteVm(data) {
      this.emitOpenModal('delete-vm', data);
    },

    actionVm(data) {
      this.emitOpenModal('action-vm', data);
    },

    exportVms() {
      VSPHERE.export();
    },
  },
};
</script>
